import React from 'react';
import { useTranslation } from 'react-i18next';

const trainingContent = [
  {
    category: 'User Manuals',
    icon: 'ri-book-open-line',
    items: [
      {
        title: 'APDPMS - Citizen User Manual',
        description: 'Step-by-step guide for citizens to apply for building permissions, track applications, and download approved plans through APDPMS portal.',
        type: 'PDF',
        icon: 'ri-file-pdf-2-line',
      },
      {
        title: 'APDPMS - Technical Person Manual',
        description: 'Guide for licensed architects and engineers to submit building plans, structural drawings, and technical documents through the portal.',
        type: 'PDF',
        icon: 'ri-file-pdf-2-line',
      },
      {
        title: 'LRS Application - User Guide',
        description: 'Complete guide for submitting Layout Regularisation Scheme (LRS) applications online, including document upload and fee payment.',
        type: 'PDF',
        icon: 'ri-file-pdf-2-line',
      },
      {
        title: 'BPS Application - User Guide',
        description: 'Guide for applying under the Building Penalisation Scheme (BPS) including self-assessment of violations and fee calculation.',
        type: 'PDF',
        icon: 'ri-file-pdf-2-line',
      },
    ],
  },
  {
    category: 'Video Tutorials',
    icon: 'ri-video-line',
    items: [
      {
        title: 'How to Register on APDPMS Portal',
        description: 'Video walkthrough of the citizen registration process on the APDPMS portal including Aadhaar verification and profile setup.',
        type: 'Video',
        icon: 'ri-play-circle-line',
      },
      {
        title: 'How to Apply for Building Permission',
        description: 'Complete video tutorial on filling the building permission application, uploading documents, and making fee payment.',
        type: 'Video',
        icon: 'ri-play-circle-line',
      },
      {
        title: 'How to Track Application Status',
        description: 'Video guide for tracking the status of submitted applications and understanding different stages of processing.',
        type: 'Video',
        icon: 'ri-play-circle-line',
      },
      {
        title: 'How to Apply for Layout Approval',
        description: 'Step-by-step video tutorial for submitting layout approval applications through the APDPMS portal.',
        type: 'Video',
        icon: 'ri-play-circle-line',
      },
    ],
  },
  {
    category: 'Fee Structure & Charges',
    icon: 'ri-money-rupee-circle-line',
    items: [
      {
        title: 'Building Permission Fee Structure',
        description: 'Detailed fee structure for building permissions including scrutiny fees, development charges, and betterment charges for different categories.',
        type: 'PDF',
        icon: 'ri-file-pdf-2-line',
      },
      {
        title: 'Layout Approval Charges',
        description: 'Fee structure for layout approval including development charges, infrastructure charges, and compound wall deposits.',
        type: 'PDF',
        icon: 'ri-file-pdf-2-line',
      },
      {
        title: 'LRS Fee Calculator',
        description: 'Guidelines for calculating LRS charges based on plot area, market value, and location classification.',
        type: 'PDF',
        icon: 'ri-file-pdf-2-line',
      },
      {
        title: 'BPS Penalty Calculator',
        description: 'Guide for calculating BPS penalisation charges based on the type of violation and extent of deviation.',
        type: 'PDF',
        icon: 'ri-file-pdf-2-line',
      },
    ],
  },
  {
    category: 'Application Forms',
    icon: 'ri-file-download-line',
    items: [
      {
        title: 'Building Permission Application Form',
        description: 'Standard application form for building permission with checklist of required documents.',
        type: 'PDF',
        icon: 'ri-file-pdf-2-line',
      },
      {
        title: 'Layout Approval Application Form',
        description: 'Application form for layout approval along with checklist of enclosures.',
        type: 'PDF',
        icon: 'ri-file-pdf-2-line',
      },
      {
        title: 'Occupancy Certificate Application Form',
        description: 'Application form for obtaining occupancy/completion certificate after construction is complete.',
        type: 'PDF',
        icon: 'ri-file-pdf-2-line',
      },
      {
        title: 'Change of Land Use (CLU) Application Form',
        description: 'Application form for requesting change of land use from the designated zoning in the master plan.',
        type: 'PDF',
        icon: 'ri-file-pdf-2-line',
      },
      {
        title: 'Architect/Engineer Registration Form',
        description: 'Form for registration of architects and engineers as licensed technical persons with DTCP.',
        type: 'PDF',
        icon: 'ri-file-pdf-2-line',
      },
    ],
  },
  {
    category: 'Research Studies',
    icon: 'ri-article-line',
    items: [
      {
        title: 'Urban Growth Trends in Andhra Pradesh',
        description: 'Study on urbanisation patterns, population growth trends, and spatial expansion of major cities in Andhra Pradesh.',
        type: 'PDF',
        icon: 'ri-file-pdf-2-line',
      },
      {
        title: 'Housing Demand Assessment Report',
        description: 'Assessment of housing demand across major urban areas including affordable housing requirements and supply analysis.',
        type: 'PDF',
        icon: 'ri-file-pdf-2-line',
      },
      {
        title: 'Infrastructure Gap Analysis for UDAs',
        description: 'Analysis of infrastructure gaps in Urban Development Authority areas covering roads, water supply, drainage, and amenities.',
        type: 'PDF',
        icon: 'ri-file-pdf-2-line',
      },
    ],
  },
];

export const TrainingPage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Breadcrumb */}
      <nav className="flex items-center space-x-2 text-sm text-stone-500 mb-8">
        <a href="/" className="hover:text-amber-700 transition-colors">{t('header.home')}</a>
        <i className="ri-arrow-right-s-line text-stone-400"></i>
        <a href="/resources" className="hover:text-amber-700 transition-colors">{t('header.resources')}</a>
        <i className="ri-arrow-right-s-line text-stone-400"></i>
        <span className="text-stone-800 font-medium">{t('header.resourcesTraining')}</span>
      </nav>

      {/* Page Header */}
      <div className="mb-12">
        <h1 className="text-3xl md:text-4xl font-bold text-stone-800 mb-4">{t('header.resourcesTraining')}</h1>
        <p className="text-lg text-stone-600 max-w-3xl">
          User manuals, video tutorials, fee structures, downloadable forms, and research studies for DTCP services and online portals.
        </p>
      </div>

      {/* Content */}
      {trainingContent.map((section) => (
        <div key={section.category} className="mb-12">
          <h2 className="text-xl font-bold text-stone-800 mb-6 flex items-center">
            <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center mr-3">
              <i className={`${section.icon} w-4 h-4 flex items-center justify-center text-amber-600`}></i>
            </div>
            {section.category}
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {section.items.map((item, index) => (
              <div
                key={index}
                className="group bg-white border border-stone-200 rounded-lg p-5 hover:shadow-md hover:border-amber-300 transition-all duration-200 cursor-pointer"
              >
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-red-100 transition-colors">
                    <i className={`${item.icon} w-5 h-5 flex items-center justify-center ${item.type === 'Video' ? 'text-blue-600' : 'text-red-500'}`}></i>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-stone-800 font-semibold group-hover:text-amber-700 transition-colors">
                        {item.title}
                      </h3>
                      <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                        item.type === 'Video'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-red-100 text-red-600'
                      }`}>
                        {item.type}
                      </span>
                    </div>
                    <p className="text-sm text-stone-500 leading-relaxed">{item.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
